import { useContext, useEffect, useMemo, useState } from 'react';

import { DEBOUNCE_TIME } from '@core/constants/common';
import { SlabContext } from '@core/context/SlabContext';
import Enums from '@core/types/api/Enums';
import { hasValue } from '@core/utils/equality';
import type { NestedKeyOf, Path } from '@core/utils/types';

import { useDebouncedCallback } from '../../hooks/useDebouncedCallback';
import { useDebouncedEffect } from '../../hooks/useDebouncedEffect';
import type { MakeToastBuilderReturn } from '../../providers/ToastProvider/utils/makeToastBuilder';
import type { FilterByOpts, NamedFilterOpts, SearchFilterOpts } from '../../utils/ApiClient';
import type { TabLabel } from './components/FilterTabs';
import type { SortConfig } from './components/SortHelpers';
import type { FilterCheckbox } from './DataTable';
import { type FilterTabLabelsOpts, useFilterTabLabels } from './hooks/useFilterTabLabels';
import { type Column, HashFilter, type TableDataModel } from './TableDataModel';
import type { ReadableSet } from './useApiTableData';
import { type DataTablePreservedState, useTablePreservedState } from './useTablePreservedState';

export type DataTableState<TRow extends { id: string }> = {
  sort: SortConfig<TRow>;
  searchFilter?: SearchFilterOpts;
  checkboxMap: Map<string, { config: FilterCheckbox<TRow>; enabled: boolean }>;
  yearFilter?: NamedFilterOpts<TRow>;
  otherFilters?: FilterByOpts<TRow>[];
  tabLabels?: TabLabel<TRow>[];
  tabFilterTemplate?: Omit<FilterByOpts<TRow>, 'values'>;
  tabFilterSavedValues?: string[];
  selectedRowIDs: readonly string[];
  renderedColumns: ReadonlySet<Column<TRow>>;
};

const isSearchFilter = <TRow extends {}>(filter: FilterByOpts<TRow>): boolean =>
  filter.operation === Enums.FilterOperation.Search;

const isYearFilter = <TRow extends {}>(filter: FilterByOpts<TRow>): boolean =>
  filter.operation === Enums.FilterOperation.Equals && filter.name === 'year';

const isTabFilter = <TRow extends {}>(
  filter: FilterByOpts<TRow>,
  tabFilterTemplate: Omit<FilterByOpts<TRow>, 'values'>,
): boolean =>
  Object.entries(tabFilterTemplate)
    .map(([property, value]) => filter[property] === value)
    .every((bool) => bool);

/** Returns the subset of filters that don't semantically match any specific properties of DataTableState. */
const findOtherFilters = <TRow extends { id: string }>(
  filters: FilterByOpts<TRow>[],
  {
    checkboxMap,
    tabFilterTemplate,
  }: {
    checkboxMap: DataTableState<TRow>['checkboxMap'];
    tabFilterTemplate?: Omit<FilterByOpts<TRow>, 'values'>;
  },
): FilterByOpts<TRow>[] => {
  const checkboxFilters: FilterByOpts<TRow>[] = [...checkboxMap.values()].map(
    ({ config }) => config.filter,
  );
  return filters.filter(
    (f) =>
      !isSearchFilter(f) &&
      !isYearFilter(f) &&
      (!hasValue(tabFilterTemplate) || !isTabFilter(f, tabFilterTemplate)) &&
      !checkboxFilters.includes(f),
  );
};

const preservedStateToTableStateAndFilters = <TRow extends { id: string }>(
  tablePreservedState: DataTablePreservedState<TRow>,
  state: DataTableState<TRow>,
  hasPermissions: (permissions?: Enums.Permission[]) => boolean,
): {
  state: DataTableState<TRow>;
  filters: FilterByOpts<TRow>[];
  columnOrder?: NestedKeyOf<TRow>[];
} => {
  // Apply settings from any valid properties of the preserved state.
  const {
    sort,
    searchFilter,
    activeTabFilterValues,
    renderedColumns,
    yearFilter: preservedYearFilter,
    checkboxFilters,
    columnOrder,
    columnFilters,
    marketFilter,
    locationFilter,
    divisionFilter,
  } = tablePreservedState;

  const newState = { ...state };
  const filters: FilterByOpts<TRow>[] = [];

  if (hasValue(columnFilters)) {
    columnFilters.forEach((it) => filters.push(it));
  }

  [marketFilter, locationFilter, divisionFilter]
    .filter(hasValue)
    .forEach((f) => filters.push(f as FilterByOpts<TRow>));

  // Parse preserved values into `state` and `newFilters`.
  if (sort !== undefined) {
    newState.sort = { ...sort, name: sort.name as Path<TRow> };
  }

  if (preservedYearFilter !== undefined) {
    filters.push(preservedYearFilter);
    newState.yearFilter = preservedYearFilter;
  } else if (state.yearFilter !== undefined) {
    filters.push(state.yearFilter);
  }

  if (state.tabLabels !== undefined && state.tabFilterTemplate !== undefined) {
    if (activeTabFilterValues !== undefined && activeTabFilterValues.length > 0) {
      const tabFilter = {
        ...state.tabFilterTemplate,
        values: activeTabFilterValues,
      } as FilterByOpts<TRow>;
      filters.push(tabFilter);
      newState.tabFilterSavedValues = activeTabFilterValues as string[];
    } else if (state.tabFilterSavedValues !== undefined) {
      const tabFilter = {
        ...state.tabFilterTemplate,
        values: state.tabFilterSavedValues,
      } as FilterByOpts<TRow>;
      filters.push(tabFilter);
    }
  }

  if (searchFilter !== undefined) {
    filters.push(searchFilter);
    newState.searchFilter = searchFilter;
  } else if (state.searchFilter !== undefined) {
    filters.push(state.searchFilter);
  }

  if (checkboxFilters !== undefined) {
    filters.push(...checkboxFilters);
    newState.checkboxMap = new Map(
      [...state.checkboxMap.entries()].map(([label, box]) => [
        label,
        {
          ...box,
          enabled:
            checkboxFilters.find((f) => HashFilter(f) === HashFilter(box.config.filter)) !==
            undefined,
        },
      ]),
    );
  } else {
    filters.push(
      ...[...state.checkboxMap.values()]
        .filter(({ config }) => config.defaultChecked ?? false)
        .map(({ config }) => config.filter),
    );
  }

  if (state.otherFilters !== undefined) {
    filters.push(...state.otherFilters);
  }

  if (renderedColumns !== undefined && renderedColumns.length > 0) {
    newState.renderedColumns = new Set(
      renderedColumns
        .filter((renderedColumn) => hasPermissions(renderedColumn.permissions))
        .filter((renderedColumn) => renderedColumn.isDisplayed),
    ) as ReadonlySet<Column<TRow>>;
  }

  return { state: newState, filters, columnOrder };
};

const withUnsavedColumnsInPlace = <TRow extends { id: string }>(
  savedOrder: NestedKeyOf<TRow>[],
  defaultOrder: NestedKeyOf<TRow>[],
): NestedKeyOf<TRow>[] => {
  const merged = [...savedOrder];
  defaultOrder.forEach((id, index) => {
    if (merged.includes(id)) {
      return;
    }
    const previous = defaultOrder
      .slice(0, index)
      .reverse()
      .find((candidate) => merged.includes(candidate));
    merged.splice(hasValue(previous) ? merged.indexOf(previous) + 1 : 0, 0, id);
  });
  return merged;
};

const setTableStateAndFilters = async <TRow extends { id: string }, TAgg extends { id: string }>(
  tableData: TableDataModel<TRow, TAgg>,
  tableState: DataTableState<TRow>,
  filters: FilterByOpts<TRow>[],
  columnOrder?: NestedKeyOf<TRow>[],
): Promise<void> => {
  tableData.setSortConfig(tableState.sort);
  await tableData.setFilters(...filters);
  tableData.allColumns.forEach((column) => {
    const isUnknownToSavedState =
      hasValue(columnOrder) && columnOrder.length > 0 && !columnOrder.includes(column.id);
    tableData.setIsColumnRendered(
      column.id,
      isUnknownToSavedState
        ? column.isDisplayed
        : [...tableState.renderedColumns].find((rendered) => column.id === rendered.id) !==
            undefined,
    );
  });
  if (columnOrder !== undefined && columnOrder.length > 0) {
    await tableData.setColumnOrder(
      withUnsavedColumnsInPlace(
        columnOrder,
        [...tableData.allColumns].map((column) => column.id),
      ),
    );
  }
};

type UseTableStateParams<TRow extends { id: string }, TAgg extends { id: string }> = {
  tableName?: string;
  tableData: TableDataModel<TRow, TAgg>;
  filterCheckboxes?: Array<FilterCheckbox<TRow>>;
  filterTabs?: FilterTabLabelsOpts<TRow, TAgg>;
  defaultFilters?: FilterByOpts<TRow>[];
  toast: MakeToastBuilderReturn;
  allColumns: ReadableSet<Column<TRow>>;
  onClearState?: () => void;
};

type UseTableStateReturn<TRow extends { id: string }> = {
  tableState: DataTableState<TRow>;
  setSelectedRowIDs: (rowIDs: readonly string[]) => void;
  setSearchString: (searchString: string) => void;
  setYear: (year: string | null) => void;
  toggleTabIsActive: (tab: TabLabel<TRow>) => Promise<void>;
  saveState: () => void;
  clearState: () => Promise<void>;
  page: number;
  setPage: (newPage: number) => void;
};

export const useTableState = <TRow extends { id: string }, TAgg extends { id: string }>({
  tableName,
  tableData,
  filterCheckboxes = [],
  filterTabs,
  defaultFilters = [],
  toast,
  onClearState = (): void => {},
}: UseTableStateParams<TRow, TAgg>): UseTableStateReturn<TRow> => {
  const { userInfo } = useContext(SlabContext);

  const [tablePreservedState, setTablePreservedState, deleteTablePreservedState] =
    useTablePreservedState<TRow>(tableName);
  const [useValuesFromInitialState, setUseValuesFromInitialState] = useState(true);

  // On subsequent renders, we use possibly-user-configured values from
  // tableData, not the initialDataTableState's values.
  useEffect(() => {
    setUseValuesFromInitialState(false);
  }, []);

  const defaultDataTableState = useMemo((): DataTableState<TRow> => {
    const searchFilter = defaultFilters.find((f) => isSearchFilter(f)) as SearchFilterOpts;
    const yearFilter = defaultFilters.find((f) => isYearFilter(f)) as NamedFilterOpts<TRow>;

    const checkboxMap = new Map(
      filterCheckboxes.map((config) => [
        config.label,
        { config, enabled: config.defaultChecked ?? false },
      ]),
    );
    const tabFilterTemplate = filterTabs?.filterTemplate;

    const otherFilters = findOtherFilters(defaultFilters, { checkboxMap, tabFilterTemplate });

    return {
      sort: tableData.initialSortConfig,
      renderedColumns: tableData.renderedColumns as ReadonlySet<Column<TRow>>,
      checkboxMap,
      searchFilter,
      yearFilter,
      otherFilters,
      selectedRowIDs: [],
      tabLabels: [],
      tabFilterTemplate,
    };
  }, []);

  // Get starting values from tablePreservedState if possible.
  // Otherwise use the table's standard defaults.
  const initialDataTableState = useMemo((): DataTableState<TRow> => {
    if (tableName === undefined) {
      return defaultDataTableState;
    }

    const { state, filters, columnOrder } = preservedStateToTableStateAndFilters(
      tablePreservedState,
      defaultDataTableState,
      userInfo.hasPermissions,
    );

    // Do not await, because we need the returned `state` to be synchronously
    // available and don't want to block rendering on the filter change.
    setTableStateAndFilters(tableData, state, filters, columnOrder);

    return state;
  }, []);

  const [selectedRowIDs, setSelectedRowIDs] = useState<readonly string[]>(
    initialDataTableState.selectedRowIDs,
  );
  // The rawSearchString is immediately updated to reflect the current value of
  // the search text input, and then a debounced update effect propagates its
  // changes into tableData's filters.
  const [rawSearchString, setRawSearchString] = useState(
    initialDataTableState.searchFilter?.values?.[0] ?? '',
  );
  const {
    filters: tabLabelFilters,
    tabs: tabLabels,
    toggleTabActive,
    resetActiveTabs,
  } = useFilterTabLabels(
    filterTabs === undefined
      ? undefined
      : {
          ...filterTabs,
          savedValues: initialDataTableState.tabFilterSavedValues,
        },
  );

  const toggleTabIsActive = async (tabLabel: TabLabel<TRow>): Promise<void> => {
    const filtersToRemove = tabLabelFilters;
    const filtersToAdd = toggleTabActive(tabLabel);
    await tableData.replaceFilters(filtersToRemove, filtersToAdd);
  };

  // Debounce search input changes to table filters
  useDebouncedEffect(
    async (): Promise<void> => {
      const searchStringFilter: FilterByOpts<TRow> = {
        operation: Enums.FilterOperation.Search,
        values: [rawSearchString],
      };
      if (rawSearchString === '') {
        await tableData.removeFilters(searchStringFilter);
      } else {
        await tableData.addFilters(searchStringFilter);
      }
    },
    [rawSearchString],
    DEBOUNCE_TIME,
  );

  // The displayed page updates immediately on click so the pagination control
  // stays responsive, while a debounced callback propagates the change into
  // tableData — collapsing rapid clicks into a single request for the final page.
  const [displayPage, setDisplayPage] = useState(tableData.pageIndex);

  // Sync back when pageIndex changes externally (e.g. filters/sort/rowsPerPage reset it to 0).
  useEffect(() => {
    setDisplayPage(tableData.pageIndex);
  }, [tableData.pageIndex]);

  const debouncedSetPageIndex = useDebouncedCallback((page: number) => {
    tableData.setPageIndex(page);
  }, DEBOUNCE_TIME);

  const setPage = (newPage: number): void => {
    setDisplayPage(newPage);
    debouncedSetPageIndex(newPage);
  };

  const checkboxMap = useValuesFromInitialState
    ? initialDataTableState.checkboxMap
    : new Map(
        filterCheckboxes.map((fcb) => [
          fcb.label,
          { config: fcb, enabled: tableData.getFilter(fcb.filter) !== undefined },
        ]),
      );

  // Year lookup filter
  const yearFilter = useValuesFromInitialState
    ? initialDataTableState.yearFilter
    : (tableData.getFilter({
        name: 'year' as Path<TRow>,
        operation: Enums.FilterOperation.Equals,
      }) as NamedFilterOpts<TRow> | undefined);

  const setYear = async (year: string | null): Promise<void> => {
    const filter: FilterByOpts<TRow> = {
      name: 'year' as Path<TRow>,
      operation: Enums.FilterOperation.Equals,
      values: [year],
    };
    if (year === null) {
      await tableData.removeFilters(filter);
    } else {
      await tableData.addFilters(filter);
    }
  };

  // Search filter
  const currentSearchFilter = useValuesFromInitialState
    ? initialDataTableState.searchFilter
    : (tableData.getFilter({
        operation: Enums.FilterOperation.Search,
      }) as SearchFilterOpts | undefined);

  // Other filters - follow the same pattern as other filter types
  const currentOtherFilters = useValuesFromInitialState
    ? initialDataTableState.otherFilters
    : findOtherFilters(tableData.getAllColumnFilters(), {
        checkboxMap,
        tabFilterTemplate: filterTabs?.filterTemplate,
      });

  const getColumnFilter = (): FilterByOpts<TRow>[] => {
    const predefinedFilters: Path<TRow>[] = [
      ...new Set(
        [...filterCheckboxes].flatMap((it) =>
          it.filter !== null && it.filter !== undefined && 'name' in it.filter
            ? [it.filter.name as Path<TRow>]
            : [],
        ),
      ),
      'marketId' as Path<TRow>,
      'plantId' as Path<TRow>,
      'locationId' as Path<TRow>,
      'divisionId' as Path<TRow>,
    ];

    return tableData
      .getAllColumnFilters()
      .filter((it) => 'name' in it && !predefinedFilters.includes(it?.name as Path<TRow>));
  };

  // Preserved state management
  const saveState = (): void => {
    if (tableName === undefined) {
      return;
    }

    const marketFilter = tableData.getFilter({
      name: 'marketId' as Path<TRow>,
      operation: Enums.FilterOperation.Equals,
    }) as NamedFilterOpts<TRow> | undefined;

    const locationFilter = (tableData.getFilter({
      name: 'plantId' as Path<TRow>,
      operation: Enums.FilterOperation.Equals,
    }) ??
      tableData.getFilter({
        name: 'locationId' as Path<TRow>,
        operation: Enums.FilterOperation.Equals,
      })) as NamedFilterOpts<TRow> | undefined;

    const divisionFilter = tableData.getFilter({
      name: 'divisionId' as Path<TRow>,
      operation: Enums.FilterOperation.Equals,
    }) as NamedFilterOpts<TRow> | undefined;

    setTablePreservedState({
      name: tableName,
      sort: tableData.sortConfig,
      searchFilter: currentSearchFilter,
      yearFilter,
      marketFilter,
      locationFilter,
      divisionFilter,
      columnFilters: getColumnFilter(),
      checkboxFilters: [...checkboxMap.entries()]
        .filter(([, v]) => v.enabled)
        .map(([, v]) => v.config.filter),
      activeTabFilterValues: tabLabelFilters.flatMap((f) => f.values),
      renderedColumns: [...tableData.renderedColumns],
      columnOrder: [...tableData.allColumns].map((col) => col.id),
    });
    toast.success({ message: 'Filters saved' });
  };

  const clearState = async (): Promise<void> => {
    if (tableName === undefined) {
      return;
    }
    deleteTablePreservedState();
    const { state, filters } = preservedStateToTableStateAndFilters(
      {},
      defaultDataTableState,
      userInfo.hasPermissions,
    );
    await setTableStateAndFilters(tableData, state, [...filters, ...resetActiveTabs()]);
    onClearState();
  };

  // Returned current state
  const tableState: DataTableState<TRow> = {
    sort: tableData.sortConfig,
    searchFilter: currentSearchFilter,
    checkboxMap,
    yearFilter,
    otherFilters: currentOtherFilters,
    tabFilterTemplate: initialDataTableState.tabFilterTemplate,
    tabLabels,
    tabFilterSavedValues: initialDataTableState.tabFilterSavedValues,
    selectedRowIDs,
    renderedColumns: new Set(
      [...tableData.renderedColumns].filter((col) => userInfo.hasPermissions(col.permissions)),
    ) as ReadonlySet<Column<TRow>>,
  };

  return {
    tableState,
    setSearchString: setRawSearchString,
    setYear,
    setSelectedRowIDs,
    toggleTabIsActive,
    saveState,
    clearState,
    page: displayPage,
    setPage,
  };
};
